/* Style Mobo — AJAX Cart + Cart Drawer */

/* ── Cart dock — sticky bottom mini-cart ── */
const SMCartDock = {
  _lastCart: null,
  _DISMISS_KEY: 'sm_cart_dock_dismissed',

  /* Persisted across reloads — set when the user explicitly closes the dock
     with its × button, so it doesn't keep popping back up on every page
     load. Cleared whenever a fresh add-to-cart happens, since that's a new
     event worth surfacing again. */
  isDismissed() {
    try { return sessionStorage.getItem(this._DISMISS_KEY) === '1'; } catch { return false; }
  },
  setDismissed(val) {
    try {
      if (val) sessionStorage.setItem(this._DISMISS_KEY, '1');
      else sessionStorage.removeItem(this._DISMISS_KEY);
    } catch {}
  },

  show(cart, opts) {
    if (!cart || cart.item_count === 0) { this.hide(); return; }
    if (opts && opts.fresh) this.setDismissed(false);
    this._lastCart = cart;
    this.hideCue();

    const dock = document.getElementById('sm-cart-dock');
    if (!dock) return;

    const lastItem = cart.items[0];
    const nameEl  = document.getElementById('sm-cart-dock-name');
    const metaEl  = document.getElementById('sm-cart-dock-meta');
    const imgWrap = document.getElementById('sm-cart-dock-img');

    if (nameEl) nameEl.textContent = lastItem?.product_title || 'Your Cart';
    if (metaEl) {
      metaEl.textContent = `${cart.item_count} item${cart.item_count !== 1 ? 's' : ''} · ${SMUtils.formatMoney(cart.total_price)}`;
    }
    if (imgWrap && lastItem?.image) {
      const src = lastItem.image.replace(/(_\d+x\d+)?\.(jpg|png|webp)(\?|$)/i, '_80x80.$2$3');
      imgWrap.innerHTML = `<img src="${src}" alt="" width="52" height="52" loading="eager">`;
    }

    dock.removeAttribute('hidden');
    requestAnimationFrame(() => requestAnimationFrame(() => dock.classList.add('is-visible')));
  },

  restore() {
    if (this._lastCart && this._lastCart.item_count > 0 && !this.isDismissed()) this.show(this._lastCart);
  },

  hide() {
    const dock = document.getElementById('sm-cart-dock');
    if (!dock) return;
    dock.classList.remove('is-visible');
    setTimeout(() => dock.setAttribute('hidden', ''), 400);
  },

  /* Small "View Cart" bubble anchored to the header/tabbar cart icon —
     shown instead of the full dock once it's been dismissed, so the cart
     stays reachable without the dock reappearing unasked. */
  showCue() {
    document.querySelectorAll('[data-sm-cart-cue]').forEach(el => el.hidden = false);
  },
  hideCue() {
    document.querySelectorAll('[data-sm-cart-cue]').forEach(el => el.hidden = true);
  }
};

document.addEventListener('click', e => {
  const cue = e.target.closest('[data-sm-cart-cue]');
  if (!cue) return;
  e.stopImmediatePropagation();
  SMCartDock.hideCue();
  document.dispatchEvent(new CustomEvent('sm:cart:open'));
});

/* ── Coupons — bar + full list + detail sheet ──
   Config comes from the "Coupon" blocks on the SM Cart Drawer section
   (see sm-cart-drawer.liquid), read once as JSON. Everything else — which
   coupon is the "best" one to surface, which are locked, which one is
   currently applied — is computed here from the live cart object on every
   sm:cart:updated, so applying/removing a code (via SMCartCoupon, now AJAX
   — see stylemobo-utils.js) never needs a page reload or a section fetch. */
const SMCoupons = {
  _config: [],
  _bound: false,

  init() {
    const el = document.getElementById('sm-coupon-config');
    if (el) {
      try { this._config = (JSON.parse(el.textContent) || []).filter(c => c.code || c.headline); }
      catch { this._config = []; }
    }
    if (this._bound) return;
    this._bound = true;
    this._bindPanel();
    this._bindDetailSheet();
    this._bindClicks();
  },

  /* The cart-value threshold has to be checked against the selling-price
     subtotal BEFORE any cart-level discount — cart.total_price is already
     reduced by whichever code is currently applied, so comparing against
     that would make applying a smaller coupon knock you out of a bigger
     one you'd otherwise still qualify for. */
  _preDiscountTotal(cart) {
    return cart.original_total_price ?? cart.total_price;
  },

  eligible(c, cart) {
    if (!cart) return false;
    const v = Number(c.rule_value) || 0;
    if (c.rule_type === 'item_count') return cart.item_count >= v;
    if (c.rule_type === 'cart_amount') return this._preDiscountTotal(cart) >= v * 100;
    return true;
  },

  appliedEntry(cart) {
    const apps = (cart && cart.cart_level_discount_applications) || [];
    for (const c of this._config) {
      if (!c.code) continue;
      const match = apps.find(a => (a.title || '').trim().toUpperCase() === c.code.trim().toUpperCase());
      if (match) return { config: c, application: match };
    }
    return null;
  },

  render(cart) {
    if (!this._bound) this.init();
    this._lastCart = cart;
    const barWrap = document.getElementById('sm-coupon-bar-wrap');
    const listRoot = document.getElementById('sm-coupon-list-root');
    if (!barWrap) return;

    if (!cart || cart.item_count === 0) {
      barWrap.innerHTML = '';
      if (listRoot) listRoot.innerHTML = '';
      return;
    }

    const applied = this.appliedEntry(cart);
    barWrap.innerHTML = this._barHtml(cart, applied);
    if (listRoot) {
      /* Only a code-type applied coupon can actually be cleared here —
         an automatic one has no Remove path (see appliedEntry/remove). */
      const clearing = this._pending && this._pending.type === 'remove';
      const clearRow = (applied && applied.config.discount_type !== 'automatic')
        ? `<button type="button" class="sm-coupon-clear-all" data-sm-coupon-remove ${clearing ? 'disabled' : ''}>${clearing ? this._icon('spinner') : 'Clear'}</button>`
        : '';
      listRoot.innerHTML = clearRow + this._listHtml(cart, applied);
    }
    const cartValueEl = document.getElementById('sm-coupon-panel-cart-value');
    if (cartValueEl) cartValueEl.textContent = `Cart value · ${SMUtils.formatMoney(cart.total_price)}`;
  },

  _icon(name) {
    /* "percent" — two dots + a slash, the universal discount glyph (Zomato-
       style badge placement, our own accent color, no copied artwork). */
    if (name === 'percent') return '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><line x1="15" y1="5" x2="5" y2="15"/><circle cx="6.5" cy="6.5" r="1.4" fill="currentColor" stroke="none"/><circle cx="13.5" cy="13.5" r="1.4" fill="currentColor" stroke="none"/></svg>';
    if (name === 'check') return '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 10.5l4 4 8-9"/></svg>';
    if (name === 'chevron') return '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M7.5 4.5l6 5.5-6 5.5"/></svg>';
    if (name === 'info') return '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="10" cy="10" r="7.25"/><path d="M10 9v4.5M10 6.85h.01"/></svg>';
    if (name === 'spinner') return '<svg class="sm-spin" viewBox="0 0 20 20" fill="none"><circle cx="10" cy="10" r="8" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-dasharray="30 100"/></svg>';
    return '';
  },

  /* What to show in place of the coupon's own description once it's no
     longer eligible — how much more is needed to unlock it. */
  _unlockMessage(c, cart) {
    if (c.rule_type === 'cart_amount') {
      const remainingCents = Math.max(0, (Number(c.rule_value) || 0) * 100 - this._preDiscountTotal(cart));
      return `Add eligible items worth ${SMUtils.formatMoney(remainingCents)} more to unlock`;
    }
    if (c.rule_type === 'item_count') {
      const remaining = Math.max(0, (Number(c.rule_value) || 0) - cart.item_count);
      return `Add ${remaining} more eligible item${remaining !== 1 ? 's' : ''} to unlock`;
    }
    return 'Add eligible items to cart to unlock';
  },

  /* Best-effort ₹ figure for "Save ₹X with this code" — merchants type a
     free-text headline like "Flat ₹200 Off", so pull the first ₹ amount
     out of it rather than requiring a separate numeric field. */
  _headlineAmount(c) {
    const match = (c.headline || '').match(/₹\s?([\d,]+)/);
    return match ? match[1].replace(/,/g, '') : null;
  },

  _barHtml(cart, applied) {
    const pending = this._pending;
    if (applied) {
      const amt = SMUtils.formatMoney(applied.application.total_allocated_amount || 0);
      const isAuto = applied.config.discount_type === 'automatic';
      const removing = pending && pending.type === 'remove';
      return `
        <div class="sm-coupon-bar sm-coupon-bar--applied">
          <span class="sm-coupon-bar__icon sm-coupon-bar__icon--check">${this._icon('check')}</span>
          <span class="sm-coupon-bar__text">
            <span class="sm-coupon-bar__title">You saved ${amt} with '${SMUtils.escapeHtml(applied.config.code)}'</span>
            <button type="button" class="sm-coupon-bar__view" data-sm-coupon-open>View all coupons ${this._icon('chevron')}</button>
          </span>
          ${isAuto ? '' : `<button type="button" class="sm-coupon-bar__action sm-coupon-bar__action--remove" data-sm-coupon-remove ${removing ? 'disabled' : ''}>${removing ? this._icon('spinner') : 'Remove'}</button>`}
        </div>`;
    }

    /* Automatic discounts apply themselves — Shopify's own tiers/progress
       bar already covers "how close am I", so this prominent bar only
       ever promotes a discount CODE the shopper can actually tap Apply on.
       Among several eligible codes, lead with the highest ₹ value (parsed
       from each headline) rather than whichever comes first in the block
       list — the shopper should see their best available deal first. */
    const eligibleList = this._config
      .filter(c => this.eligible(c, cart) && c.discount_type !== 'automatic' && c.code)
      .sort((a, b) => (Number(this._headlineAmount(b)) || 0) - (Number(this._headlineAmount(a)) || 0));
    const best = eligibleList[0] || this._config.find(c => this.eligible(c, cart) && c.discount_type !== 'automatic');

    if (best) {
      const label = best.promo_line || best.headline || 'Special offer';
      const applying = pending && pending.type === 'apply' && pending.code === best.code;
      return `
        <div class="sm-coupon-bar">
          <span class="sm-coupon-bar__icon">${this._icon('percent')}</span>
          <span class="sm-coupon-bar__text">
            <span class="sm-coupon-bar__title">${SMUtils.escapeHtml(label)}${best.code ? ` with '${SMUtils.escapeHtml(best.code)}'` : ''}</span>
            <button type="button" class="sm-coupon-bar__view" data-sm-coupon-open>View all coupons ${this._icon('chevron')}</button>
          </span>
          ${best.code ? `<button type="button" class="sm-coupon-bar__action sm-coupon-bar__action--apply" data-sm-coupon-code="${encodeURIComponent(best.code)}" ${applying ? 'disabled' : ''}>${applying ? this._icon('spinner') : 'APPLY'}</button>` : ''}
        </div>`;
    }

    const subtext = this._config.length
      ? `${this._config.length} offer${this._config.length !== 1 ? 's' : ''} — add items to unlock`
      : '';
    return `
      <button type="button" class="sm-coupon-summary" data-sm-coupon-open aria-haspopup="true" aria-controls="sm-coupon-panel">
        <span class="sm-coupon-summary__icon">${this._icon('percent')}</span>
        <span class="sm-coupon-summary__text">
          <span class="sm-coupon-summary__title">View Coupons</span>
          ${subtext ? `<span class="sm-coupon-summary__sub">${subtext}</span>` : ''}
        </span>
        <span class="sm-coupon-summary__chevron">${this._icon('chevron')}</span>
      </button>`;
  },

  _listHtml(cart, applied) {
    if (!this._config.length) {
      return '<p class="sm-coupon-empty">No coupons set up yet — add a "Coupon" block to this section in the theme editor.</p>';
    }
    return this._config.map((c, i) => {
      const isApplied = !!(applied && applied.config === c);
      /* Real Shopify state always wins over our own rule_value guess — if
         Shopify already has this discount active on the cart, never show it
         as locked, even if our client-side threshold check disagrees (e.g.
         the merchant's "Minimum value" here doesn't exactly match the real
         discount's minimum in Admin). */
      const isEligible = isApplied || this.eligible(c, cart);
      const isAuto = c.discount_type === 'automatic';
      const hasInfo = c.terms || c.headline || c.description;
      const infoBtn = hasInfo
        ? `<button type="button" class="sm-coupon-card__info" data-sm-coupon-info="${i}" aria-label="Coupon details">${this._icon('info')}</button>`
        : '';

      /* Subtext: applied beats everything and always states the real,
         Shopify-confirmed savings; otherwise the merchant's own
         description/promo line if they wrote one, falling back to a
         "Save ₹X with this code" built from the headline's own ₹ figure so
         something accurate shows even with the text fields left blank. */
      const headlineAmount = this._headlineAmount(c);
      let subtext;
      if (isApplied) {
        const savedAmount = SMUtils.formatMoney(applied.application.total_allocated_amount || 0);
        subtext = `You saved ${savedAmount} with this code`;
      } else if (isEligible) {
        subtext = c.description || c.promo_line
          || (headlineAmount ? `Save ₹${headlineAmount} with this code` : '');
      } else {
        subtext = this._unlockMessage(c, cart);
      }
      const subtextClass = isEligible ? 'sm-coupon-card__desc' : 'sm-coupon-card__desc sm-coupon-card__desc--locked';

      /* Automatic discounts apply/remove themselves — Shopify decides, not
         a tap here — so they never get the radio-to-apply affordance or a
         clickable row, just a static "Auto" tag once eligible. */
      const applyingThis = this._pending && this._pending.type === 'apply' && this._pending.code === c.code;
      let action = applyingThis
        ? `<span class="sm-coupon-card__radio sm-coupon-card__radio--loading">${this._icon('spinner')}</span>`
        : '<span class="sm-coupon-card__radio"></span>';
      if (isApplied) {
        action = `<span class="sm-coupon-card__radio sm-coupon-card__radio--on">${this._icon('check')}</span>`;
      } else if (isAuto && isEligible) {
        action = '<span class="sm-coupon-card__auto-tag">Auto</span>';
      }

      let tapAttr = '';
      if (!isApplied && !(isAuto && isEligible)) {
        tapAttr = (isEligible && c.code)
          ? ` data-sm-coupon-code="${encodeURIComponent(c.code)}"`
          : ' data-sm-coupon-locked="1"';
      }

      return `
        <div class="sm-coupon-card${!isEligible ? ' sm-coupon-card--locked' : ''}${isApplied ? ' sm-coupon-card--applied' : ''}"${tapAttr}>
          <div class="sm-coupon-card__row">
            <span class="sm-coupon-card__badge">${this._icon('percent')}</span>
            <span class="sm-coupon-card__body">
              <span class="sm-coupon-card__headline-row">
                <span class="sm-coupon-card__headline">${SMUtils.escapeHtml(c.headline || c.code || '')}</span>
                ${infoBtn}
              </span>
              ${subtext ? `<span class="${subtextClass}">${SMUtils.escapeHtml(subtext)}</span>` : ''}
              ${c.code ? `<span class="sm-coupon-card__code-box">${SMUtils.escapeHtml(c.code)}</span>` : ''}
            </span>
            ${action}
          </div>
        </div>`;
    }).join('');
  },

  openPanel() {
    const panel = document.getElementById('sm-coupon-panel');
    if (!panel) return;
    panel.hidden = false;
    requestAnimationFrame(() => requestAnimationFrame(() => panel.classList.add('is-open')));
  },

  closePanel() {
    const panel = document.getElementById('sm-coupon-panel');
    if (!panel) return;
    panel.classList.remove('is-open');
    setTimeout(() => { panel.hidden = true; }, 320);
  },

  _bindPanel() {
    document.getElementById('sm-coupon-back')?.addEventListener('click', () => this.closePanel());
  },

  openDetail(index) {
    const c = this._config[index];
    if (!c) return;
    const sheet = document.getElementById('sm-coupon-detail-sheet');
    const overlay = document.getElementById('sm-coupon-detail-overlay');
    if (!sheet) return;
    document.getElementById('sm-coupon-detail-headline').textContent = c.headline || c.code || '';
    const codeText = document.getElementById('sm-coupon-detail-code-text');
    if (codeText) codeText.textContent = c.code || '';
    document.getElementById('sm-coupon-detail-code').hidden = !c.code;
    const termsEl = document.getElementById('sm-coupon-detail-terms');
    if (termsEl) {
      const lines = (c.terms || '').split('\n').map(t => t.trim()).filter(Boolean);
      termsEl.innerHTML = lines.map(t => `<li>${this._icon('check')}<span>${SMUtils.escapeHtml(t)}</span></li>`).join('');
    }
    sheet.hidden = false;
    if (overlay) overlay.hidden = false;
    requestAnimationFrame(() => requestAnimationFrame(() => {
      sheet.classList.add('is-open');
      overlay?.classList.add('is-active');
    }));
  },

  closeDetail() {
    const sheet = document.getElementById('sm-coupon-detail-sheet');
    const overlay = document.getElementById('sm-coupon-detail-overlay');
    sheet?.classList.remove('is-open');
    overlay?.classList.remove('is-active');
    setTimeout(() => {
      if (sheet) sheet.hidden = true;
      if (overlay) overlay.hidden = true;
    }, 280);
  },

  _bindDetailSheet() {
    document.getElementById('sm-coupon-detail-okay')?.addEventListener('click', () => this.closeDetail());
    document.getElementById('sm-coupon-detail-overlay')?.addEventListener('click', () => this.closeDetail());
    document.getElementById('sm-coupon-detail-code')?.addEventListener('click', () => {
      const code = document.getElementById('sm-coupon-detail-code-text')?.textContent || '';
      if (!code) return;
      const done = () => window.SMToast?.show('Code copied!');
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(code).then(done).catch(done);
      } else {
        done();
      }
    });
  },

  _bindClicks() {
    document.addEventListener('click', e => {
      /* One apply/remove in flight at a time — ignore further taps until
         the spinner clears, rather than letting them race. */
      const isPendingTarget = e.target.closest('[data-sm-coupon-remove], [data-sm-coupon-code]');
      if (this._pending && isPendingTarget) return;

      const info = e.target.closest('[data-sm-coupon-info]');
      if (info) {
        e.stopPropagation();
        this.openDetail(parseInt(info.dataset.smCouponInfo, 10));
        return;
      }
      const remove = e.target.closest('[data-sm-coupon-remove]');
      if (remove) {
        const current = this.appliedEntry(this._lastCart);
        this._pending = { type: 'remove', code: current?.config?.code || null };
        this.render(this._lastCart);
        window.SMCartCoupon?.remove(current?.config?.code).finally(() => {
          this._pending = null;
          this.render(this._lastCart);
        });
        return;
      }
      const codeEl = e.target.closest('[data-sm-coupon-code]');
      if (codeEl) {
        const code = decodeURIComponent(codeEl.dataset.smCouponCode || '');
        if (code) {
          this._pending = { type: 'apply', code };
          this.render(this._lastCart);
          window.SMCartCoupon?.apply(code).finally(() => {
            this._pending = null;
            this.render(this._lastCart);
          });
        }
        this.closePanel();
        return;
      }
      const opener = e.target.closest('[data-sm-coupon-open]');
      if (opener) {
        this.openPanel();
        return;
      }
      const locked = e.target.closest('[data-sm-coupon-locked]');
      if (locked) {
        const msg = locked.querySelector('.sm-coupon-card__desc--locked');
        if (msg) {
          msg.classList.remove('is-shaking');
          void msg.offsetWidth; // restart the animation on repeat taps
          msg.classList.add('is-shaking');
        }
        return;
      }
    });
  }
};

class SMCartDrawer extends HTMLElement {
  connectedCallback() {
    this._overlay = document.querySelector('.sm-overlay');
    this._closeBtn = this.querySelector('.sm-cart-drawer__close');
    this._continueBtn = this.querySelector('.sm-cart-continue');
    this._itemsContainer = this.querySelector('.sm-cart-drawer__body');
    this._cartCount = document.querySelectorAll('[data-sm-cart-count]');
    this._upsellSection = this.querySelector('.sm-cart-upsell');

    const tiersEl = this.querySelector('.sm-tiers');
    this._tiersData = [];
    if (tiersEl) {
      try { this._tiersData = JSON.parse(tiersEl.dataset.tiers || '[]'); } catch { this._tiersData = []; }
    }
    this._unlockedTiers = new Set(
      [...this.querySelectorAll('.sm-tiers__checkpoint.is-unlocked')].map(el => el.dataset.checkpoint)
    );

    this._closeBtn?.addEventListener('click', () => this.close());
    this._continueBtn?.addEventListener('click', () => this.close());
    this._overlay?.addEventListener('click', () => this.close());

    document.addEventListener('keydown', e => {
      if (e.key !== 'Escape') return;
      if (this._quickview && !this._quickview.hidden) { this._closeQuickview(); return; }
      if (this.classList.contains('is-open')) this.close();
    });

    document.addEventListener('sm:cart:open', () => this.open());
    document.addEventListener('sm:cart:updated', e => this._render(e.detail));

    this._itemsContainer?.addEventListener('click', e => {
      const btn = e.target.closest('[data-sm-qty]');
      if (btn) this._changeQty(btn.dataset.line, parseInt(btn.dataset.smQty));

      const rm = e.target.closest('[data-sm-remove]');
      if (rm) this._changeQty(rm.dataset.line, 0);
    });

    this.addEventListener('click', e => {
      const upsellAdd = e.target.closest('[data-sm-upsell-add]');
      if (upsellAdd) {
        this._quickAddOrOpen(upsellAdd);
        return;
      }

      if (e.target.closest('[data-sm-add-more]')) { this.close(); return; }

      const qvCard = e.target.closest('[data-quickview]');
      if (qvCard) { this._openQuickview(qvCard); return; }

      if (e.target.closest('[data-qv-close]')) this._closeQuickview();
    });

    this._quickview = document.getElementById('sm-quickview');
    const qvAdd = document.getElementById('sm-quickview-add');
    qvAdd?.addEventListener('click', () => {
      if (!this._qvVariantId || qvAdd.disabled) return;
      const properties = this._collectQvProperties();
      this._setBtnLoading(qvAdd, true);
      this._addItem(this._qvVariantId, this._qvCompareAt, properties)
        .finally(() => { this._setBtnLoading(qvAdd, false); this._closeQuickview(); });
    });
  }

  /* ── "+" quick-add: adds instantly for simple products, but for products
       that actually need Brand/Model/Material/Custom Text, blind-adding the
       first variant would be wrong — open the quick-view popup with a picker
       instead so the shopper chooses before it lands in the cart. ── */
  async _quickAddOrOpen(btn) {
    const needsPicker = btn.dataset.hasVariants === 'true' || btn.dataset.hasCustomText === 'true';
    if (!needsPicker) {
      this._setBtnLoading(btn, true);
      try { await this._addItem(btn.dataset.variantId, btn.dataset.compareAt); }
      finally { this._setBtnLoading(btn, false); }
      return;
    }

    const card = btn.closest('[data-quickview]');
    if (card) {
      this._openQuickview(card);
    } else if (btn.dataset.productHandle) {
      this._openQuickviewByHandle(btn.dataset.productHandle);
    }
  }

  _setBtnLoading(btn, loading) {
    if (!btn) return;
    const hasIconOnly = !!btn.querySelector('svg') && !btn.textContent.trim();
    if (loading) {
      if (!hasIconOnly && !btn.dataset.smOrigText) {
        btn.dataset.smOrigText = btn.textContent.trim();
        btn.textContent = 'Adding…';
      }
      btn.classList.add('is-loading');
      btn.disabled = true;
    } else {
      if (btn.dataset.smOrigText) {
        btn.textContent = btn.dataset.smOrigText;
        delete btn.dataset.smOrigText;
      }
      btn.classList.remove('is-loading');
      btn.disabled = false;
    }
  }

  _openQuickview(card) {
    if (!this._quickview) return;
    this._paintQuickviewBasics(card.dataset);
    this._quickview.hidden = false;
    document.querySelector('.sm-quickview-overlay')?.removeAttribute('hidden');

    const needsOptions = card.dataset.hasVariants === 'true' || card.dataset.hasCustomText === 'true';
    if (needsOptions && card.dataset.productHandle) {
      this._loadQuickviewOptions(card.dataset.productHandle, false);
    } else {
      this._qvVariantId = card.dataset.qvVariantId;
      this._qvCompareAt = card.dataset.qvCompareAt;
      this._hideQuickviewOptions();
    }
  }

  /* ── Opens quick-view for a product with no pre-rendered card (e.g. the
       Steal Deals "+" button, which adds directly and has no click-to-view
       wrapper) — fetches everything fresh from /products/{handle}.js. ── */
  async _openQuickviewByHandle(handle) {
    if (!this._quickview) return;
    this._paintQuickviewBasics({ qvTitle: 'Loading…', qvImage: '', qvPrice: '', qvDesc: '' });
    this._quickview.hidden = false;
    document.querySelector('.sm-quickview-overlay')?.removeAttribute('hidden');
    await this._loadQuickviewOptions(handle, true);
  }

  _paintQuickviewBasics(data) {
    const img = document.getElementById('sm-quickview-img');
    const title = document.getElementById('sm-quickview-title');
    const price = document.getElementById('sm-quickview-price');
    const desc = document.getElementById('sm-quickview-desc');
    if (img) { img.src = data.qvImage || ''; img.alt = data.qvTitle || ''; }
    if (title) title.textContent = data.qvTitle || '';
    if (price) price.textContent = data.qvPrice || '';
    if (desc) desc.textContent = data.qvDesc || '';
  }

  /* ── Fetches the product's real variants (rendering a dropdown per option
       if it has more than one) AND, for products tagged custom-text/dual-text,
       the same Yes/No + text-field personalization the product page offers —
       so quick-adds from the side cart never skip it. Add stays disabled
       until everything required is answered. ── */
  async _loadQuickviewOptions(handle, paintFull) {
    const optionsWrap = document.getElementById('sm-quickview-options');
    const addBtn = document.getElementById('sm-quickview-add');
    if (!optionsWrap) return;

    this._qvProduct = null;
    this._qvSelections = null;
    this._qvVariantId = null;
    this._qvCompareAt = 0;
    this._qvHasCustomText = false;
    this._qvHasDualText = false;
    optionsWrap.hidden = false;
    optionsWrap.innerHTML = '<div class="sm-quickview__options-loading">Loading options…</div>';
    if (addBtn) { addBtn.disabled = true; addBtn.textContent = 'Select options'; }

    try {
      const res = await fetch(`/products/${handle}.js`);
      const product = await res.json();

      if (paintFull) {
        this._paintQuickviewBasics({
          qvTitle: product.title,
          qvImage: product.featured_image || (product.images[0] || ''),
          qvPrice: SMUtils.formatMoney(product.price),
          qvDesc: (product.description || '').replace(/<[^>]*>/g, '').slice(0, 200)
        });
      }

      const tagsLower = (product.tags || []).join('|').toLowerCase();
      const hasDualText = tagsLower.includes('dual-text');
      const hasCustomText = hasDualText || tagsLower.includes('custom-text');
      this._qvHasCustomText = hasCustomText;
      this._qvHasDualText = hasDualText;

      const hasRealOptions = product.variants.length > 1;
      let html = '';

      if (hasRealOptions) {
        this._qvProduct = product;
        this._qvSelections = new Array(product.options.length).fill(null);
        html += product.options.map((name, i) => {
          const values = [...new Set(product.variants.map(v => v.options[i]))];
          return `
            <div class="sm-quickview__opt" data-qv-opt="${i}">
              <label class="sm-quickview__opt-label">${SMUtils.escapeHtml(name)}</label>
              <select class="sm-quickview__opt-select" data-qv-opt-select="${i}">
                <option value="" selected>Please select ${SMUtils.escapeHtml(name.toLowerCase())}</option>
                ${values.map(v => `<option value="${SMUtils.escapeHtml(v)}">${SMUtils.escapeHtml(v)}</option>`).join('')}
              </select>
            </div>
          `;
        }).join('');
      } else {
        const v = product.variants[0];
        this._qvVariantId = v.id;
        this._qvCompareAt = v.compare_at_price || 0;
      }

      if (hasCustomText) {
        html += `
          <div class="sm-quickview__opt" data-qv-ct-group>
            <label class="sm-quickview__opt-label">Custom Text on Cover</label>
            <div class="sm-quickview__ct-toggle">
              <label class="sm-quickview__ct-radio">
                <input type="radio" name="sm-qv-ct-toggle" value="no">
                <span>No Custom Text</span>
              </label>
              <label class="sm-quickview__ct-radio">
                <input type="radio" name="sm-qv-ct-toggle" value="yes" data-qv-ct-yes>
                <span>Yes, Add Custom Text</span>
              </label>
            </div>
            <div class="sm-quickview__ct-fields" data-qv-ct-fields hidden>
              <label class="sm-quickview__opt-label sm-quickview__opt-label--sub">
                Enter your Custom Text you want to print
                <span class="sm-quickview__ct-count" data-qv-ct-count="1">0 / 30</span>
              </label>
              <input type="text" class="sm-quickview__opt-select" data-qv-ct-field="1" maxlength="30" placeholder="e.g. Alex, MJ, Love…" autocomplete="off">
              ${hasDualText ? `
                <label class="sm-quickview__opt-label sm-quickview__opt-label--sub">
                  Enter your custom initials
                  <span class="sm-quickview__ct-count" data-qv-ct-count="2">0 / 10</span>
                </label>
                <input type="text" class="sm-quickview__opt-select" data-qv-ct-field="2" maxlength="10" placeholder="e.g. EC" autocomplete="off">
              ` : ''}
            </div>
          </div>
        `;
      }

      if (!hasRealOptions && !hasCustomText) {
        this._hideQuickviewOptions();
        return;
      }

      optionsWrap.innerHTML = html;

      optionsWrap.querySelectorAll('[data-qv-opt-select]').forEach(sel => {
        sel.addEventListener('change', () => this._syncQuickviewState());
      });

      optionsWrap.querySelectorAll('input[name="sm-qv-ct-toggle"]').forEach(radio => {
        radio.addEventListener('change', () => {
          const fields = optionsWrap.querySelector('[data-qv-ct-fields]');
          const isYes = !!optionsWrap.querySelector('[data-qv-ct-yes]:checked');
          if (fields) fields.hidden = !isYes;
          if (!isYes) {
            optionsWrap.querySelectorAll('[data-qv-ct-field]').forEach(f => { f.value = ''; });
            optionsWrap.querySelectorAll('[data-qv-ct-count]').forEach(c => {
              const field = optionsWrap.querySelector(`[data-qv-ct-field="${c.dataset.qvCtCount}"]`);
              c.textContent = `0 / ${field ? field.maxLength : 30}`;
            });
          }
          this._syncQuickviewState();
        });
      });

      optionsWrap.querySelectorAll('[data-qv-ct-field]').forEach(field => {
        field.addEventListener('input', () => {
          const counter = optionsWrap.querySelector(`[data-qv-ct-count="${field.dataset.qvCtField}"]`);
          if (counter) counter.textContent = `${field.value.length} / ${field.maxLength}`;
        });
      });

      this._syncQuickviewState();
    } catch {
      optionsWrap.innerHTML = '<div class="sm-quickview__options-error">Couldn’t load options — try again.</div>';
    }
  }

  _syncQuickviewState() {
    const optionsWrap = document.getElementById('sm-quickview-options');
    const addBtn = document.getElementById('sm-quickview-add');
    if (!optionsWrap) return;

    let variant = null;
    if (this._qvProduct) {
      optionsWrap.querySelectorAll('[data-qv-opt-select]').forEach(sel => {
        const i = parseInt(sel.dataset.qvOptSelect);
        this._qvSelections[i] = sel.value || null;
      });
      variant = this._qvProduct.variants.find(v =>
        v.options.every((opt, i) => opt === this._qvSelections[i])
      ) || null;

      const priceEl = document.getElementById('sm-quickview-price');
      if (variant) {
        this._qvVariantId = variant.id;
        this._qvCompareAt = variant.compare_at_price || 0;
        if (priceEl) priceEl.textContent = SMUtils.formatMoney(variant.price);
      } else {
        this._qvVariantId = null;
      }
    }

    const ctAnswered = this._qvHasCustomText
      ? !!optionsWrap.querySelector('input[name="sm-qv-ct-toggle"]:checked')
      : true;

    if (!addBtn) return;
    if (!this._qvVariantId) {
      addBtn.disabled = true;
      addBtn.textContent = 'Select options';
    } else if (variant && !variant.available) {
      addBtn.disabled = true;
      addBtn.textContent = 'Sold out';
    } else if (!ctAnswered) {
      addBtn.disabled = true;
      addBtn.textContent = 'Select options';
    } else {
      addBtn.disabled = false;
      addBtn.textContent = 'Add to cart';
    }
  }

  /* ── Line-item properties from the quick-view's Custom Text step, matching
       the product page's format so cart rows render identically either way. ── */
  _collectQvProperties() {
    if (!this._qvHasCustomText) return null;
    const optionsWrap = document.getElementById('sm-quickview-options');
    if (!optionsWrap) return null;
    const isYes = !!optionsWrap.querySelector('[data-qv-ct-yes]:checked');
    const props = {};
    const f1 = optionsWrap.querySelector('[data-qv-ct-field="1"]');
    props['Custom Text'] = isYes && f1 ? f1.value : '';
    if (this._qvHasDualText) {
      const f2 = optionsWrap.querySelector('[data-qv-ct-field="2"]');
      props['Custom Initials'] = isYes && f2 ? f2.value : '';
    }
    return props;
  }

  _hideQuickviewOptions() {
    const optionsWrap = document.getElementById('sm-quickview-options');
    const addBtn = document.getElementById('sm-quickview-add');
    if (optionsWrap) { optionsWrap.hidden = true; optionsWrap.innerHTML = ''; }
    if (addBtn) { addBtn.disabled = false; addBtn.textContent = 'Add to cart'; }
  }

  _closeQuickview() {
    if (this._quickview) this._quickview.hidden = true;
    document.querySelector('.sm-quickview-overlay')?.setAttribute('hidden', '');
    this._qvProduct = null;
    this._qvSelections = null;
    this._qvHasCustomText = false;
    this._qvHasDualText = false;
    this._hideQuickviewOptions();
  }

  open() {
    this._lastFocused = document.activeElement;
    SMCartDock.hide();
    SMCartDock.hideCue();
    this.classList.add('is-open');
    this._overlay?.classList.add('is-active');
    document.body.style.overflow = 'hidden';
    SMUtils.trapFocus(this);
    this._fetchAndRender();
  }

  close() {
    this.classList.remove('is-open');
    this._overlay?.classList.remove('is-active');
    document.body.style.overflow = '';
    SMCoupons.closePanel();
    SMCoupons.closeDetail();
    if (SMCartDock.isDismissed()) {
      if (SMCartDock._lastCart && SMCartDock._lastCart.item_count > 0) SMCartDock.showCue();
    } else {
      SMCartDock.restore();
    }
    if (this._lastFocused && typeof this._lastFocused.focus === 'function') {
      this._lastFocused.focus();
    }
  }

  async _fetchAndRender() {
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      this._render(cart);
    } catch {}
  }

  _render(cart) {
    this._updateCount(cart.item_count);
    this._updateTiers(cart);
    SMCoupons.render(cart);
    if (cart.item_count === 0) {
      this._renderEmpty();
    } else {
      this._renderItems(cart);
    }
  }

  _renderEmpty() {
    const body = this.querySelector('.sm-cart-drawer__body');
    const footer = this.querySelector('.sm-cart-drawer__footer');
    const summary = this.querySelector('.sm-cart-drawer__summary');
    const progress = this.querySelector('.sm-cart-progress');
    const coupons = this.querySelector('.sm-cart-coupons');
    const summaryHeading = this.querySelector('.sm-cart-order-summary-heading');
    const estTotal = document.getElementById('sm-cart-est-total');
    const banner = this.querySelector('[data-sm-saved-banner]');
    if (banner) banner.hidden = true;
    if (summaryHeading) summaryHeading.hidden = true;
    if (body) body.innerHTML = `
      <div class="sm-cart-drawer__empty">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
          <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/>
          <line x1="3" y1="6" x2="21" y2="6"/>
          <path d="M16 10a4 4 0 01-8 0"/>
        </svg>
        <h3>Your cart is empty</h3>
        <p>Add something beautiful to get started.</p>
        <button class="btn-sm-primary sm-cart-continue" style="font-size:1.5rem;padding:1.4rem 3rem;">
          Start Shopping
        </button>
      </div>
    `;
    if (footer) footer.hidden = true;
    if (summary) summary.hidden = true;
    if (progress) progress.hidden = true;
    if (coupons) coupons.hidden = true;
    if (estTotal) estTotal.hidden = true;
    SMCartDock.hide();
    SMCartDock.hideCue();
    SMCartDock._lastCart = null;
    SMCartDock.setDismissed(false);
    this.querySelector('.sm-cart-continue')?.addEventListener('click', () => this.close());
  }

  async _getCompareAtMap(items) {
    this._compareCache = this._compareCache || {};
    const handles = [...new Set(
      items.filter(i => !(i.variant_id in this._compareCache)).map(i => i.handle)
    )];
    await Promise.all(handles.map(async handle => {
      try {
        const res = await fetch(`/products/${handle}.js`);
        const product = await res.json();
        product.variants.forEach(v => { this._compareCache[v.id] = v.compare_at_price; });
      } catch {}
    }));
    return this._compareCache;
  }

  async _renderItems(cart) {
    const footer = this.querySelector('.sm-cart-drawer__footer');
    const summary = this.querySelector('.sm-cart-drawer__summary');
    const progress = this.querySelector('.sm-cart-progress');
    const coupons = this.querySelector('.sm-cart-coupons');
    const summaryHeading = this.querySelector('.sm-cart-order-summary-heading');
    if (footer) footer.hidden = false;
    if (summary) summary.hidden = false;
    if (progress) progress.hidden = false;
    if (coupons) coupons.hidden = false;
    if (summaryHeading) summaryHeading.hidden = false;


    const body = this.querySelector('.sm-cart-drawer__body');
    if (!body) return;

    const compareMap = await this._getCompareAtMap(cart.items);

    body.innerHTML = cart.items.map((item, idx) => {
      const compareAt = compareMap[item.variant_id] || 0;
      const compareLine = compareAt > item.price ? compareAt * item.quantity : 0;
      return `
      <div class="sm-cart-item" data-line="${idx + 1}">
        <div class="sm-cart-item__img">
          <img
            src="${item.image ? item.image.replace('.jpg', '_240x240.jpg').replace('.png', '_240x240.png') : ''}"
            alt="${item.product_title}"
            width="120" height="120"
            loading="lazy"
          >
        </div>
        <div class="sm-cart-item__info">
          <div class="sm-cart-item__top-row">
            <div class="sm-cart-item__titles">
              <a href="${item.url}" class="sm-cart-item__title">${item.product_title}</a>
              ${(() => {
                  const values = (item.options_with_values || [])
                    .filter(o => o.value !== 'Default Title')
                    .map(o => SMUtils.escapeHtml(o.value));
                  return values.length ? `<span class="sm-cart-item__variant">${values.join(' · ')}</span>` : '';
                })()}
              ${item.properties ? Object.entries(item.properties)
                  .filter(([key, value]) => value || key === 'Custom Text' || key === 'Custom Initials')
                  .map(([key, value]) => {
                    const isCustom = key === 'Custom Text' || key === 'Custom Initials';
                    if (isCustom && !value) return `<span class="sm-cart-item__property">No Custom Text</span>`;
                    return `<span class="sm-cart-item__property">${SMUtils.escapeHtml(key)}: ${SMUtils.escapeHtml(value)}</span>`;
                  })
                  .join('')
                : ''}
            </div>
            <button class="sm-cart-item__remove" data-sm-remove data-line="${idx + 1}" aria-label="Remove ${item.product_title}">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
                <path d="M4 7h16"/>
                <path d="M9 7V4.5A1.5 1.5 0 0110.5 3h3A1.5 1.5 0 0115 4.5V7"/>
                <path d="M6 7l1 13a2 2 0 002 2h6a2 2 0 002-2l1-13"/>
              </svg>
            </button>
          </div>
          <div class="sm-cart-item__price-row">
            <span class="sm-cart-item__price">${SMUtils.formatMoney(item.final_line_price)}</span>
            ${compareLine > 0
              ? `<span class="sm-cart-item__compare-price">${SMUtils.formatMoney(compareLine)}</span>`
              : ''}
          </div>
          <div class="sm-qty-control">
            <button class="sm-qty-btn" data-sm-qty="${item.quantity - 1}" data-line="${idx + 1}" aria-label="Decrease quantity">−</button>
            <span class="sm-qty-value">${item.quantity}</span>
            <button class="sm-qty-btn" data-sm-qty="${item.quantity + 1}" data-line="${idx + 1}" aria-label="Increase quantity">+</button>
          </div>
        </div>
      </div>
    `;
    }).join('') + `
      <button class="sm-cart-add-more" data-sm-add-more type="button">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="16" height="16">
          <line x1="12" y1="5" x2="12" y2="19"/>
          <line x1="5" y1="12" x2="19" y2="12"/>
        </svg>
        Add more items
      </button>
    `;

    /* Totals — shared calculations for both the old in-scroll block and the
       sticky "To Pay" collapsible bar outside the scroll area. */
    const mrp = cart.items.reduce((s, i) => {
      const rawCompare = compareMap[i.variant_id];
      const compareAt = (rawCompare && rawCompare > i.price) ? rawCompare : i.price;
      return s + compareAt * i.quantity;
    }, 0);
    const preDiscount = cart.items.reduce((s, i) => s + i.original_line_price, 0);
    const productSavings = mrp - preDiscount;
    const couponDiscount = (cart.cart_level_discount_applications || [])
      .reduce((s, d) => s + (d.total_allocated_amount || 0), 0);
    const totalSavings = productSavings + couponDiscount;

    const totals = this.querySelector('.sm-cart-totals');
    if (totals) {
      totals.innerHTML = `
        <div class="sm-cart-totals__row">
          <span>Item Total</span>
          <span>${SMUtils.formatMoney(mrp)}</span>
        </div>
        ${productSavings > 0 ? `
          <div class="sm-cart-totals__row sm-cart-totals__row--saving">
            <span>Savings</span>
            <span>−${SMUtils.formatMoney(productSavings)}</span>
          </div>` : ''}
        ${cart.cart_level_discount_applications
          .filter(d => d.title && Number.isFinite(d.total_allocated_amount) && d.total_allocated_amount > 0)
          .map(d => `
          <div class="sm-cart-totals__row sm-cart-totals__row--saving">
            <span>${SMUtils.escapeHtml(d.title)}</span>
            <span>−${SMUtils.formatMoney(d.total_allocated_amount)}</span>
          </div>`).join('')}
        <div class="sm-cart-totals__row" style="color:var(--sm-ink-secondary);font-size:1.2rem">
          <span>Shipping</span>
          <span>Calculated at checkout</span>
        </div>
        <div class="sm-cart-totals__row sm-cart-totals__row--total">
          <div class="sm-cart-totals__total-label">
            <span>To pay</span>
            <span class="sm-cart-totals__tax-note">(Inclusive of Taxes)</span>
          </div>
          <span>${SMUtils.formatMoney(cart.total_price)}</span>
        </div>
      `;
    }

    /* "To Pay" collapsible bar — lives outside the scroll area */
    const estTotal = document.getElementById('sm-cart-est-total');
    if (estTotal) {
      estTotal.hidden = false;
      const finalEl = estTotal.querySelector('.sm-cart-est-total__final');
      if (finalEl) finalEl.textContent = SMUtils.formatMoney(cart.total_price);

      /* These spans are only present in the server-rendered markup when the
         page loaded with a non-empty/discounted cart (Liquid `{% if %}`
         blocks) — on a page that loaded with an empty cart they never exist,
         so create them on demand instead of just querying for them. */
      let mrpEl = estTotal.querySelector('.sm-cart-est-total__mrp');
      if (mrp > preDiscount) {
        if (!mrpEl) {
          mrpEl = document.createElement('span');
          mrpEl.className = 'sm-cart-est-total__mrp';
          estTotal.querySelector('.sm-cart-est-total__amounts')?.prepend(mrpEl);
        }
        mrpEl.textContent = SMUtils.formatMoney(mrp);
        mrpEl.style.display = '';
      } else if (mrpEl) {
        mrpEl.style.display = 'none';
      }

      let savedEl = estTotal.querySelector('.sm-cart-est-total__saved');
      if (totalSavings > 0) {
        if (!savedEl) {
          savedEl = document.createElement('span');
          savedEl.className = 'sm-cart-est-total__saved';
          estTotal.querySelector('.sm-cart-est-total__label')?.appendChild(savedEl);
        }
        savedEl.textContent = `You saved ${SMUtils.formatMoney(totalSavings)}!`;
        savedEl.style.display = '';
      } else if (savedEl) {
        savedEl.style.display = 'none';
      }

      const breakdown = document.getElementById('sm-cart-est-breakdown');
      if (breakdown) {
        breakdown.innerHTML = `
          <div class="sm-cart-totals__row"><span>Total MRP</span><span>${SMUtils.formatMoney(mrp)}</span></div>
          <div class="sm-cart-totals__row"><span>Delivery fee</span><span>To be calculated</span></div>
          ${productSavings > 0 ? `<div class="sm-cart-totals__row sm-cart-totals__row--saving"><span>Discount on MRP</span><span>−${SMUtils.formatMoney(productSavings)}</span></div>` : ''}
          <div class="sm-cart-totals__row${couponDiscount > 0 ? ' sm-cart-totals__row--saving' : ''}">
            <span>Coupon discount</span>
            <span>${couponDiscount > 0 ? '−' : ''}${SMUtils.formatMoney(couponDiscount)}</span>
          </div>
          <div class="sm-cart-totals__row"><span>Convenience Fee</span><span>${SMUtils.formatMoney(0)}</span></div>
          <div class="sm-cart-totals__row sm-cart-totals__row--total"><span>Grand total</span><span>${SMUtils.formatMoney(cart.total_price)}</span></div>
          ${totalSavings > 0 ? `<div class="sm-cart-est-total__banner">You Saved ${SMUtils.formatMoney(totalSavings)} so far!</div>` : ''}
        `;
      }
    }
  }

  _updateTiers(cart) {
    if (!this._tiersData || !this._tiersData.length) return;
    const totalCents = cart.original_total_price;
    const maxThreshold = Math.max(...this._tiersData.map(t => t.threshold));
    if (!maxThreshold) return;
    const fillPct = Math.min(Math.max((totalCents / maxThreshold) * 100, 0), 100);

    const fill = this.querySelector('[data-tier-fill]');
    if (fill) fill.style.width = fillPct + '%';

    const marker = this.querySelector('[data-tier-marker]');
    if (marker) marker.style.left = fillPct + '%';

    this.querySelectorAll('[data-checkpoint]').forEach(cp => {
      const key = cp.dataset.checkpoint;
      const threshold = parseInt(key || 0);
      const unlocked = totalCents >= threshold;
      const wasUnlocked = this._unlockedTiers.has(key);
      cp.classList.toggle('is-unlocked', unlocked);
      cp.innerHTML = unlocked
        ? '<svg viewBox="0 0 24 24" width="9" height="9" fill="none" stroke="#FFF" stroke-width="3.2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 13l4 4L19 7"/></svg>'
        : '';
      if (unlocked && !wasUnlocked) {
        this._unlockedTiers.add(key);
        this._confettiBurst();
      } else if (!unlocked && wasUnlocked) {
        this._unlockedTiers.delete(key);
      }
    });

    const status = this.querySelector('[data-tier-status]');
    if (status) {
      const sorted = [...this._tiersData].sort((a, b) => a.threshold - b.threshold);
      const next = sorted.find(t => totalCents < t.threshold);
      const current = [...sorted].reverse().find(t => totalCents >= t.threshold);
      status.classList.toggle('is-complete', !next);
      if (next) {
        const remaining = Math.round((next.threshold - totalCents) / 100);
        status.textContent = `₹${remaining} away from ${next.label} on your order`;
      } else if (current) {
        status.textContent = `${current.label} applied on your order`;
      } else {
        status.textContent = 'Add items to unlock automatic discounts';
      }
    }
  }

  _confettiBurst() {
    /* Rains across the whole cart drawer, not just the tier checkpoint —
       a discount unlocking is worth a bigger celebration than a tiny pop. */
    const rect = this.getBoundingClientRect();
    const colors = ['#0A0A0A', '#C9A84C', '#E3242B', '#1A7A35', '#FFFFFF'];
    const container = document.createElement('div');
    container.className = 'sm-confetti-burst';
    document.body.appendChild(container);
    const count = 60;
    for (let i = 0; i < count; i++) {
      const p = document.createElement('span');
      p.className = 'sm-confetti-piece';
      const startX = rect.left + Math.random() * rect.width;
      const startY = rect.top - 20 - Math.random() * 40;
      const fallDistance = rect.height * (0.55 + Math.random() * 0.55);
      const drift = Math.random() * 140 - 70;
      const size = 5 + Math.random() * 5;
      p.style.left = startX + 'px';
      p.style.top = startY + 'px';
      p.style.width = size + 'px';
      p.style.height = (size * 0.4) + 'px';
      p.style.background = colors[i % colors.length];
      p.style.setProperty('--dx', drift + 'px');
      p.style.setProperty('--dy', fallDistance + 'px');
      p.style.setProperty('--rot', (Math.random() * 720 - 360) + 'deg');
      p.style.animationDuration = (900 + Math.random() * 600) + 'ms';
      p.style.animationDelay = Math.round(Math.random() * 300) + 'ms';
      container.appendChild(p);
    }
    setTimeout(() => container.remove(), 1900);
  }

  _updateCount(count) {
    this._cartCount.forEach(el => {
      el.textContent = count;
      el.setAttribute('data-count', count);
    });
    const badge = this.querySelector('.sm-cart-drawer__count');
    if (badge) badge.textContent = count;
  }


  async _changeQty(line, qty) {
    const item = this.querySelector(`.sm-cart-item[data-line="${line}"]`);
    if (item) item.classList.add('is-loading');

    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line: parseInt(line), quantity: qty })
      });
      /* /cart/change.js's own response can return incomplete
         cart_level_discount_applications in the same request a discount
         newly becomes eligible (a known Shopify quirk) — always follow up
         with a clean GET so totals render from fully-resolved cart data. */
      const cartRes = await fetch('/cart.js');
      const cart = await cartRes.json();
      document.dispatchEvent(new CustomEvent('sm:cart:updated', { detail: cart }));
      if (window.Shopify?.PaymentButton) window.Shopify.PaymentButton.init();
    } catch {
      SMToast.show('Something went wrong. Please try again.');
    } finally {
      if (item) item.classList.remove('is-loading');
    }
  }

  async _addItem(variantId, compareAt, properties) {
    /* We already know this product's compare-at price from the server-rendered
       card (upsell/quickview/steal-deal) — seed the cache now so the render
       right after doesn't need a round-trip to /products/{handle}.js for it. */
    const compareAtNum = parseInt(compareAt, 10);
    if (variantId && compareAtNum > 0) {
      this._compareCache = this._compareCache || {};
      this._compareCache[variantId] = compareAtNum;
    }

    const body = { id: variantId, quantity: 1 };
    if (properties && Object.keys(properties).length) body.properties = properties;

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (res.ok) {
        const cartRes = await fetch('/cart.js');
        const cart = await cartRes.json();
        document.dispatchEvent(new CustomEvent('sm:cart:updated', { detail: cart }));
        SMToast.show('Added to cart!');
      }
    } catch {}
  }
}
customElements.define('sm-cart-drawer', SMCartDrawer);

/* ── Global add-to-cart interception ──
   Runs in the CAPTURE phase so it fires before Dawn's own <product-form>
   submit handler (attached directly on the form, bubble phase). We then
   stop propagation so Dawn's handler never runs at all — otherwise both
   fire, causing a duplicate /cart/add.js call, and Dawn's handler throws
   trying to find a .loading__spinner element our custom button markup
   doesn't have, leaving the button stuck disabled forever. */
document.addEventListener('submit', async e => {
  const form = e.target.closest('[data-sm-atc-form]');
  if (!form) return;
  e.preventDefault();
  e.stopPropagation();

  const formId = form.getAttribute('id');
  const picker = formId && document.querySelector(`.sm-vp[data-form-id="${formId}"]`);
  if (picker && picker.__smVP && !picker.__smVP._isComplete()) {
    picker.__smVP._goToFirstIncompleteStep();
    return;
  }

  const btn = form.querySelector('[type="submit"]');
  if (btn) { btn.disabled = true; btn.textContent = 'Adding…'; }

  try {
    const formData = new FormData(form);
    formData.set('sections', '');
    formData.set('sections_url', window.location.pathname);

    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Accept': 'application/json' },
      body: formData
    });

    if (!res.ok) {
      const err = await res.json();
      SMToast.show(err.description || 'Unable to add to cart.');
      return;
    }

    const cartRes = await fetch('/cart.js');
    const cart = await cartRes.json();
    document.dispatchEvent(new CustomEvent('sm:cart:updated', { detail: cart }));
    /* The dock itself (image + name + price + View Cart) already confirms
       the add — a plain text toast on top of it just overlaps the dock. */
    SMCartDock.show(cart, { fresh: true });
  } catch {
    SMToast.show('Something went wrong.');
  } finally {
    if (btn) { btn.disabled = false; btn.textContent = 'Add to Cart'; }
  }
}, true);

/* ── Open cart drawer from any [data-sm-cart-open] element ── */
document.addEventListener('click', e => {
  if (e.target.closest('[data-sm-cart-open]')) {
    document.dispatchEvent(new CustomEvent('sm:cart:open'));
  }
});

/* ── Header cart icon → open drawer; dock buttons; page-load dock ── */
document.addEventListener('DOMContentLoaded', () => {
  SMCoupons.init();

  // Intercept all /cart links → open drawer
  document.querySelectorAll('a[href="/cart"]').forEach(link => {
    link.addEventListener('click', e => {
      e.preventDefault();
      document.dispatchEvent(new CustomEvent('sm:cart:open'));
    });
  });

  // Dock buttons
  document.getElementById('sm-cart-dock-btn')?.addEventListener('click', () => {
    SMCartDock.hide();
    document.dispatchEvent(new CustomEvent('sm:cart:open'));
  });
  document.getElementById('sm-cart-dock-close')?.addEventListener('click', () => {
    SMCartDock.hide();
    /* Keep _lastCart — the cart still has items — but remember the dismissal
       so it doesn't auto-reappear on reload. Show the small cue instead. */
    SMCartDock.setDismissed(true);
    if (SMCartDock._lastCart && SMCartDock._lastCart.item_count > 0) {
      setTimeout(() => SMCartDock.showCue(), 400);
    }
  });

  // Show dock on page load if cart has items — unless the user already
  // dismissed it this session, in which case show the small cart-icon cue.
  fetch('/cart.js')
    .then(r => r.json())
    .then(cart => {
      document.dispatchEvent(new CustomEvent('sm:cart:updated', { detail: cart }));
      if (cart.item_count > 0) {
        SMCartDock._lastCart = cart;
        if (SMCartDock.isDismissed()) SMCartDock.showCue();
        else SMCartDock.show(cart);
      }
    })
    .catch(() => {});
});
